import { t } from "i18next";
import { FC, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import FormWrapper from "@app/components/FormWrapper";
import SalesChannelForm from "@app/components/forms/SalesChannel.form";
import { RoutePaths } from "@app/routes/index";
import { createSalesChannel, updateSalesChannel } from "@app/services/Endpoints/SalesChannel.API";
import { ModalType, SalesChannelErrors } from "@app/types/Common.types";
import { SalesChannelFormData, SalesChannels } from "@app/types/SalesChannel.types";
import { SALES_CHANNEL_FORM_VALIDATION } from "@app/utils/constant";
import { FormContext } from "@app/utils/contexts/Form.context";


const initialFormData: SalesChannelFormData = {
    name: "",
    posName: "",
    weightage: 10,
    status: true
};

interface SalesChannelContainerProps {
    salesChannel?: SalesChannels;
}

const SalesChannelContainer: FC<SalesChannelContainerProps> = ({ salesChannel }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { triggerShowModal, setActions, isDirty, setIsDirty } = useContext(FormContext);

    const [salesChannelData, setsalesChannelData] = useState<SalesChannelFormData>(initialFormData);
    const [errors, setErrors] = useState<Partial<SalesChannelErrors>>();

    useEffect(() => {
        if (salesChannel) {
            const {
                id,
                name,
                posName,
                weightage,
                status
            } = {
                ...salesChannel,
            };
            setsalesChannelData({
                id,
                name,
                posName,
                weightage,
                status,
            });
        }
    }, [salesChannel]);

    const handleChange = (name: string, value: string | number | boolean) => {
        const data: SalesChannelFormData = { ...salesChannelData };

        const hasError = !!errors?.[name];
        if (hasError) {
            setErrors({ ...errors, [name]: "" });
        }

        let parsedValue: string | number | boolean = value;

        if (name === "weightage") {
            parsedValue = Number(value);
        } else if (name === "status") {
            parsedValue = Boolean(value);
        }

        setsalesChannelData({
            ...data,
            [name]: parsedValue,
        });
    };

    const handleCancel = () => {
        if (isDirty) {
            triggerShowModal(true, ModalType.CANCEL);
            setActions({
                done: () => {
                    setsalesChannelData(initialFormData);
                    setErrors(undefined);
                    navigate(RoutePaths.ViewSalesChannels);
                    triggerShowModal(false, ModalType.CANCEL);
                    setIsDirty(false);
                },
                cancel: () => {
                    triggerShowModal(false, ModalType.CANCEL);
                },
            });
        } else navigate(RoutePaths.ViewSalesChannels);
    };

    const handleCreateSalesChannel = () => {
        createSalesChannel(salesChannelData)
            .then(() => {
                triggerShowModal(false, ModalType.SUBMIT);
                navigate(RoutePaths.ViewSalesChannels);
                setIsDirty(false);
            })
            .catch((error) => {
                triggerShowModal(false, ModalType.SUBMIT);
                const response = error?.response ?? error;
                const messageCode = response?.data?.message_code;
                const message = response?.data?.message;
                if (messageCode === "INVALID_INPUT") {
                    setErrors({
                        posName: Array.isArray(message)
                            ? message[0]
                            : typeof message === "string"
                                ? message
                                : t("ADD.SALESCHANNEL.INVALID_POS_NAME"),
                    });
                } else {
                    setErrors({
                        name: error?.message ?? "",
                    });
                }
            });
    };
    const handleUpdateSalesChannel = () => {
        updateSalesChannel({
            ...salesChannelData,
            id: String(salesChannel?.id),
        })
            .then(() => {
                triggerShowModal(false, ModalType.SUBMIT);
                navigate(RoutePaths.ViewSalesChannels);
                setIsDirty(false);
            })
            .catch((error) => {
                triggerShowModal(false, ModalType.SUBMIT);
                const response = error?.response ?? error;
                const messageCode = response?.data?.message_code;
                const message = response?.data?.message;

                if (messageCode === "INVALID_INPUT") {
                    setErrors({
                        name: Array.isArray(message)
                            ? message[0]
                            : typeof message === "string"
                                ? message
                                : t("ERRORS.INVALID_INPUT"),
                    });
                }
            });
    };

    const handleSubmit = () => {
        const isEdit = !!salesChannel;
        const {
            name,
            posName,
            weightage,
        } = salesChannelData;
        const isNameValid = !!name && name.length <= 15;
        const isPosNameValid = !!posName;
        const isWeightageValid = weightage >= 1 && weightage <= 99;

        const isDataValid = isEdit
            ? isNameValid && isWeightageValid
            : isNameValid && isPosNameValid && isWeightageValid;

        if (isDataValid) {
            setErrors(undefined);
            triggerShowModal(true, ModalType.SUBMIT);
            setActions({
                done: () => isEdit ? handleUpdateSalesChannel() : handleCreateSalesChannel(),
                cancel: () => triggerShowModal(false, ModalType.SUBMIT),
            });
        } else {
            const currentErrors: Partial<SalesChannelErrors> = {};
            if (!isNameValid) {
                if (!name) {
                    currentErrors.name = `${t("ERRORS.REQUIRED", {
                        name: t("ADD.SALESCHANNEL.DISPLAY_NAME.LABEL"),
                    })}`;
                } else if (name.length > SALES_CHANNEL_FORM_VALIDATION.MAX_NAME_LENGTH) {
                    currentErrors.name = t("ADD.SALESCHANNEL.DISPLAY_NAME_MAX_LENGTH_ERROR") as string;
                }
            }
            if (!isEdit && !isPosNameValid)
                currentErrors.posName = `${t("ERRORS.REQUIRED", {
                    name: t("ADD.SALESCHANNEL.POS_NAME.LABEL"),
                })}`;
            if (!isWeightageValid)
                currentErrors.weightage = t("ADD.SALESCHANNEL.WEIGHTAGE_RANGE_ERROR") as string;
            setErrors({ ...errors, ...currentErrors });
        }
    };

    return (
        <FormWrapper
            heading={
                location.pathname.includes("edit")
                    ? t("ADD.SALESCHANNEL.EDIT_HEADING")
                    : t("ADD.SALESCHANNEL.HEADING")
            }
            onCancelClick={handleCancel}
            onSubmitClick={handleSubmit}
            submitDisabled={!isDirty}
            noteSection={true}
            noteText={t("ADD.SALESCHANNEL.NOTE_TEXT") as string}
        >
            <SalesChannelForm
                data={salesChannelData}
                onChange={handleChange}
                errors={errors}
                isEdit={!!salesChannel}
            />
        </FormWrapper>
    );
};

export default SalesChannelContainer;
